import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { Alert, App, Button, Modal, Tag, Tooltip } from 'antd'
import { z } from 'zod'
import { useStore } from 'zustand'
import { createListQueryStore } from '@/stores/list-query-store.ts'
import { useTRPC } from '@/trpc/react.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

const schema = z.object({
  current: z.number().int().positive().catch(1),
  pageSize: z.number().int().min(1).max(100).catch(20),
  keyword: z.string().optional(),
  kind: z.enum(['rental', 'service', 'group', 'lifecycle']).optional(),
  status: z.enum(['completed', 'empty', 'failed', 'running', 'skipped']).optional()
})
const store = createListQueryStore('finance:automation-runs', schema)

export function BillingAutomationRuns({ canManage, onClose }: { canManage: boolean; onClose: () => void }) {
  const [open, setOpen] = useState(true)
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const saved = useStore(store, (s) => s.searches[currentSession.id])
  const search = saved ?? schema.parse({})
  const setSearch = (value: z.infer<typeof schema>) => store.getState().remember(currentSession.id, value)
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp()
  const q = useQuery({ ...trpc.bill.automationRuns.queryOptions(search), placeholderData: keepPreviousData })
  const retry = useMutation(trpc.bill.retryAutomation.mutationOptions())
  return (
    <Modal
      open={open}
      title="自动出账与组单任务"
      width="80rem"
      footer={null}
      onCancel={() => setOpen(false)}
      afterClose={onClose}
    >
      <div className="text-content-muted mb-3 flex flex-wrap items-center gap-x-2 text-sm">
        <span>记录业务处理结果，空批次也会保留。</span>
        <Tooltip
          title="迟录费用及作废后的重新组单，请使用按客户月份补组。失败任务重试复用原业务批次。"
          trigger={['hover', 'focus', 'click']}
        >
          <Button type="link" size="small">
            处理说明
          </Button>
        </Tooltip>
      </div>
      {q.isError && <Alert type="error" title={q.error.message} />}
      <ProTable
        rowKey="id"
        dataSource={q.data?.data ?? []}
        loading={q.isFetching}
        scroll={{ x: 'max-content' }}
        form={{ initialValues: search }}
        search={{ labelWidth: 'auto' }}
        toolBarRender={false}
        columnsState={{ persistenceKey: 'finance:automation-runs:table', persistenceType: 'localStorage' }}
        onSubmit={(v) => setSearch(schema.parse({ ...v, current: 1, pageSize: search.pageSize }))}
        onReset={() => setSearch(schema.parse({}))}
        pagination={{
          ...search,
          total: q.data?.total,
          onChange: (current, pageSize) => setSearch({ ...search, current, pageSize })
        }}
        columns={[
          { title: '关键词 / ID', dataIndex: 'keyword', hideInTable: true },
          { title: 'ID', dataIndex: 'id', copyable: true, search: false, width: '5rem' },
          {
            title: '任务',
            dataIndex: 'kind',
            valueEnum: { rental: '租赁出账', service: '服务出账', group: '客户组单', lifecycle: '订单到期' },
            width: '8rem'
          },
          { title: '对象 ID', dataIndex: 'targetId', search: false, width: '6rem' },
          { title: '归属月份', dataIndex: 'billingMonth', search: false, width: '8rem' },
          {
            title: '状态',
            dataIndex: 'status',
            valueEnum: { completed: '完成', empty: '空批次', failed: '失败', running: '处理中', skipped: '已跳过' },
            width: '7rem',
            render: (_, row) => (
              <Tag color={row.status === 'failed' ? 'error' : row.status === 'completed' ? 'success' : 'default'}>
                {
                  (
                    {
                      completed: '完成',
                      empty: '空批次',
                      failed: '失败',
                      running: '处理中',
                      skipped: '已跳过'
                    } as Record<string, string>
                  )[row.status]
                }
              </Tag>
            )
          },
          { title: '尝试次数', dataIndex: 'attempts', search: false, width: '6rem' },
          { title: '业务键', dataIndex: 'businessKey', search: false, copyable: true, width: '20rem' },
          {
            title: '结果 / 原因',
            key: 'result',
            search: false,
            width: '22rem',
            render: (_, row) => row.error ?? JSON.stringify(row.result)
          },
          { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false, width: '12rem' },
          {
            fixed: 'right',
            align: 'center',
            title: '操作',
            valueType: 'option',
            width: '6rem',
            render: (_, row) =>
              canManage && row.status === 'failed' ? (
                <Button
                  type="link"
                  loading={retry.isPending}
                  onClick={async () => {
                    try {
                      await retry.mutateAsync({ runId: row.id })
                      await Promise.all([
                        refreshMoneyQueries(cache, trpc),
                        cache.invalidateQueries(trpc.bill.automationRuns.queryFilter())
                      ])
                      void message.success('任务已重试完成')
                    } catch (e) {
                      void message.error(e instanceof Error ? e.message : '重试失败')
                      await q.refetch()
                    }
                  }}
                >
                  重试
                </Button>
              ) : null
          }
        ]}
      />
    </Modal>
  )
}
